// -----------------------------------------------------------------------------
// AttendedGuidedChecklistV2  (Phase 4a — v2 animation variant)
//
// Drop-in replacement for AttendedGuidedChecklist that renders v2 sub-components
// (UpNextCardV2, ChecklistListV2) with richer directional entrance animations:
//
//   Stats strip  — slide-down from -12px (fadeSx → translateY(-12px) → none)
//   UpNext card  — slide-up from +24px (handled inside UpNextCardV2)
//   List rows    — slide from left with stagger (handled inside ChecklistListV2)
//
// All v1 gate / spawn / retry / preview logic is faithfully preserved.
// data-testid="attended-guided-checklist-v2" on the outer wrapper so tests can
// distinguish v1 from v2 renders.
//
// Activate by visiting any attended popup with ?guidedV=2 in the URL.
// The switcher lives in AttendedAppointment.jsx and
// ConsultationAttendedAppointment.jsx — this file is pure v2.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import { parseApiDate } from 'src/utils/taskDateUtils';
import { Box, CircularProgress, Typography, Button, IconButton, Snackbar, useMediaQuery, Dialog, DialogContent, DialogActions } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { dispatch } from 'src/redux/store';
import { completeChecklistItem, reopenChecklistItem } from 'src/redux/slices/taskSlice';
import { CRM_PRIMARY_BUTTON_SX } from 'src/styles/crmButtons';
import { CHECKLIST_COMPLETION_STATE, TASKS_V2_CHANGED_EVENT } from 'src/constants/tasksV2';
import { updateError } from 'src/redux/slices/user';
import { callApi } from 'src/utils/apiCall';
import cascadeTaskDueDates from 'src/utils/cascadeTaskDueDates';
import { buildCompletionPayload } from 'src/utils/checklistCompletionPayload';
import { FollowUp } from '../../../../../components/tabs';
import useGuidedChecklist from '../hooks/useGuidedChecklist';
import useUndoAttendance from '../hooks/useUndoAttendance';
import AttendanceGate from '../AttendanceGate';
import UndoBanner from '../UndoBanner';
import UpNextCardV2, { TaskAssigneeHeads } from './UpNextCardV2';
import RecentlyCompletedCard from '../RecentlyCompletedCard';
// PostSendExpandedCard removed — sequenced ack (task #22) no longer shows it
// during the 4s window; the card appears only in the steady-state post-ack view.
import RescheduleDialog from './RescheduleDialog';
import ChecklistMiniGrid from '../ChecklistMiniGrid';
import ActionPanel from '../ActionPanel';
import TasksEmptyStateCard from '../TasksEmptyStateCard';
import { INLINE_HANDLED_TYPES } from '../panels';
import FollowUpPanel from '../panels/FollowUpPanel';
import { COMPLETION_LABELS, DEFAULT_COMPLETION_LABEL } from '../actionMeta';

const EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';
const BOOKED_STATUS = 'CONSULTATION_BOOKED';
const SPAWN_INITIAL_DELAY_MS = 600;
const SPAWN_RETRY_DELAY_MS = 1000;
const SPAWN_MAX_RETRIES = 2;
// Safety-net window for clearing optimistic-done IDs if a refresh never confirms
// them (e.g. the bust-cache refetch errored) — guarantees a hidden item recovers.
const OPTIMISTIC_DONE_FALLBACK_MS = 6000;

// ---------------------------------------------------------------------------

// Spawn-failure / no-checklist fallback. There's no checklist task for this
// lead, so (1) let the user RECORD what they actually did to contact the patient
// — reusing the proven FollowUpPanel channel picker (Call · WhatsApp · Email ·
// Note); the sub-panels log the note/call to /leads/{id}/notes/ on their own, so
// a null `item` is fine — and (2) keep the manual follow-up/reminder below.
// onTaskActivity fires on mount + on any recorded action so the host's outcome
// gate (activeTab) isn't left empty and trapping Finish ("Please select any one
// Tab"). We do NOT complete a checklist item (there is none) — onComplete just
// records host activity.
function SpawnFailureFallback({
    handleClose,
    onTaskActivity,
    practiceId,
    leadId,
    leadDetail,
    onOpenSidebarTab,
    handleReloadLeads,
}) {
    useEffect(() => {
        if (onTaskActivity) onTaskActivity();
    }, [onTaskActivity]);
    const handleRecorded = () => {
        // Contact already logged by the sub-panel; just mark host activity.
        // Don't close — the follow-up scheduler stays available below.
        if (onTaskActivity) onTaskActivity();
    };
    return (
        <Box data-testid="spawn-failure-fallback" sx={{ py: 1 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1d1d1f', mb: 0.25, letterSpacing: '-0.01em' }}>
                No checklist for this lead
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#6e6e73', mb: 1, letterSpacing: '-0.005em' }}>
                Record what you did to contact the patient, then set a follow-up below.
            </Typography>
            <FollowUpPanel
                item={null}
                practiceId={practiceId}
                leadId={leadId}
                leadDetail={leadDetail}
                onClose={handleClose}
                onComplete={handleRecorded}
                handleReloadLeads={handleReloadLeads}
                onOpenSidebarTab={onOpenSidebarTab}
            />
            <Box sx={{ borderTop: '1px solid rgba(145,158,171,0.16)', mt: 1.5, pt: 1.5 }}>
                <FollowUp type="lead" handleClose={handleClose} />
            </Box>
        </Box>
    );
}
SpawnFailureFallback.propTypes = {
    handleClose: PropTypes.func,
    onTaskActivity: PropTypes.func,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadDetail: PropTypes.object,
    onOpenSidebarTab: PropTypes.func,
    handleReloadLeads: PropTypes.func,
};

// Broadcast a tasks-v2 change so the attended table's per-task date columns refetch.
// complete/skip/reopen already emit from their thunks (taskSlice); this covers the
// component-only mutations (reschedule, pause) which call the API directly.
function emitTasksV2Changed() {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(TASKS_V2_CHANGED_EVENT));
}

export default function AttendedGuidedChecklistV2({
    practiceId,
    leadId,
    leadDetail,
    mode,
    handleClose,
    handleNoAttended,
    gateRenderedInHost,
    externalGateDismissed,
    externalRefreshKey,
    handleReloadLeads,
    onTaskActivity,
    onOpenSidebarTab,
    onCreateReminder,
}) {
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    // Inline reschedule toast — MUI Snackbar rendered inside this component so
    // it shares the popup's stacking context and is never hidden behind the Dialog.
    const [rescheduledMsg, setRescheduledMsg] = useState(null);

    // Debug overlay toggle — mirrors AttendedAppointment: ?attDebug=1 OR
    // localStorage attDebug='1' OR DEV (and not explicitly '0'). When on, the
    // checklist's internal split (UpNext left + ActionPanel right) gets dashed
    // outlines + corner labels so the two sides are clearly visible. Zero change off.
    const attDebug =
        new URLSearchParams(window.location.search).get('attDebug') === '1'
        || (typeof window !== 'undefined' && window.localStorage.getItem('attDebug') === '1')
        || (import.meta.env.DEV
            && new URLSearchParams(window.location.search).get('attDebug') !== '0'
            && !(typeof window !== 'undefined' && window.localStorage.getItem('attDebug') === '0'));
    const dbgTagSx = (color) => ({
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 50,
        bgcolor: color,
        color: '#fff',
        fontSize: '9px',
        fontWeight: 700,
        lineHeight: 1.4,
        px: 0.5,
        borderBottomRightRadius: 4,
        letterSpacing: '0.04em',
        pointerEvents: 'none',
        textTransform: 'uppercase',
    });

    // Skip the gate if the lead is already CONSULTATION_ATTENDED (handles re-open).
    const initiallyAttended = (leadDetail?.lead_treatments || []).some(
        (t) => t.status === 'CONSULTATION_ATTENDED'
    );
    const [gateDismissed, setGateDismissed] = useState(initiallyAttended);
    // Undo-attendance banner — only active in gate-first mode.
    const { showBanner, secondsLeft, startUndo, undo, dismiss: dismissUndo } = useUndoAttendance();
    const [featuredOverride, setFeaturedOverride] = useState(null);
    const [actionPanelItem, setActionPanelItem] = useState(null);
    const [bodyMounted, setBodyMounted] = useState(false);
    const [spawnLoading, setSpawnLoading] = useState(false);
    const [spawnGaveUp, setSpawnGaveUp] = useState(false);
    const spawnRetryRef = useRef(0);
    // Optimistic due-date overrides: Map<taskId, Date> applied before rendering.
    const [dueDateOverrides, setDueDateOverrides] = useState(() => new Map());
    // Post-action transition state.
    const [recentlyCompleted, setRecentlyCompleted] = useState(null);
    const recentlyCompletedTimerRef = useRef(null);
    // Pause dialog — tracks which item is being paused and the resume datetime
    const [pauseDialogItem, setPauseDialogItem] = useState(null);
    const [pauseUntilDate, setPauseUntilDate] = useState('');
    // Optimistic paused-state flag — set synchronously in the POST success callback
    // so the amber Paused card renders in the SAME React batch as the state updates,
    // before any async refresh / Redux side-effects can close the popup.
    // Stores the paused item's display data so the card can render without waiting
    // for the server GET to confirm the paused state.
    const [optimisticPausedInfo, setOptimisticPausedInfo] = useState(null);

    // Re-entrancy guard for handlePanelComplete — prevents a second POST for the same
    // (taskId, itemId) pair while the first is still in-flight. Mirrors the
    // optimisticDoneIds pattern but uses a ref (not state) so the Set mutation is
    // synchronous and zero-render-cost. Key format: "${taskId}:${itemId}".
    const completingItemIdsRef = useRef(new Set());
    // Guard against pause double-fire — same pattern as completingItemIdsRef.
    const pauseInFlightRef = useRef(false);

    // Change 4 (revised): showRescheduleBanner is set TRUE on a confirmed completion so
    // the banner appears in the STEADY-STATE full-view ABOVE the next task — AFTER the
    // 4s ✓-ack split-view has cleared. NOT tied to the ack timer.
    // Reset to false on: user dismisses (✕), user confirms reschedule, new completion.
    const [showRescheduleBanner, setShowRescheduleBanner] = useState(false);
    const [bannerRescheduleOpen, setBannerRescheduleOpen] = useState(false);

    // Optimistic completion tracking — prevents stale GET data from reverting the mini-grid
    // while the busted-cache refresh is in-flight or when the BE auto-completes an item
    // asynchronously (e.g. after transactional-message send).
    // Items are added immediately on complete/skip/send; cleared once the server's done
    // group confirms them (reconcile effect below).
    const [optimisticDoneIds, setOptimisticDoneIds] = useState(() => new Set());
    // When the gate is owned internally AND the lead is already CONSULTATION_ATTENDED
    // at mount, skip the spawn-refresh sequence — tasks were created in a prior session.
    // When gateRenderedInHost=true the host drives dismissal, so never skip.
    const skipSpawnRef = useRef(!gateRenderedInHost && initiallyAttended);

    const { loading, initialLoading, templatesEmpty, groups, upNext, templatePreviewItems = [], allDoneCount, refresh } = useGuidedChecklist({
        practiceId,
        leadId,
        leadDetail,
    });

    // External refresh trigger — parent bumps externalRefreshKey when something
    // happened outside this component that the server already knows about
    // (e.g. RecordAttended landed → BE-computed due dates now exist). Skip the
    // initial 0 so we don't double-fetch on first mount alongside fetchAll.
    const lastSeenRefreshKeyRef = useRef(0);
    useEffect(() => {
        if (externalRefreshKey == null) return;
        if (externalRefreshKey === lastSeenRefreshKeyRef.current) return;
        lastSeenRefreshKeyRef.current = externalRefreshKey;
        if (externalRefreshKey > 0) refresh({ bustCache: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [externalRefreshKey]);

    const { today: rawToday = [], later: rawLater = [], done = [] } = groups || {};
    // Apply optimistic exclusion: items we know are done but the server hasn't confirmed yet.
    // Short-circuit when the set is empty (normal case) to avoid unnecessary filter passes.
    const today = optimisticDoneIds.size > 0 ? rawToday.filter(i => !optimisticDoneIds.has(i.id)) : rawToday;
    const later = optimisticDoneIds.size > 0 ? rawLater.filter(i => !optimisticDoneIds.has(i.id)) : rawLater;
    const totalCount = today.length + later.length + done.length;
    const doneCount = done.length;
    // allDone via two paths:
    //   1. BE returns completed items in checklist_items → normal count check.
    //   2. BE omits completed items (server-side filter) → allDoneCount > 0 from
    //      progress-based detection in the hook.
    const allDone = (totalCount > 0 && doneCount === totalCount) || allDoneCount > 0;
    // Detect cascade-paused state: all items returned as PAUSED (cascade pause is
    // intentional — but the UI must say "Paused", not "All tasks complete").
    // Two paths:
    //   1. optimisticPausedInfo set — fires synchronously on POST 200, shows the card
    //      before the async refresh + any popup-close side-effects can run.
    //   2. allDone + server data confirms all PAUSED — covers re-opens / page reloads.
    const allPausedFromServer = allDone && done.length > 0
        && done.every(i => i.completion_state === CHECKLIST_COMPLETION_STATE.PAUSED);
    const allPaused = !!optimisticPausedInfo || allPausedFromServer;

    const effectiveGateDismissed = gateRenderedInHost ? !!externalGateDismissed : gateDismissed;

    // Use initialLoading (not loading) so that post-action refreshes don't hide the body.
    // Once tasks have loaded once, subsequent loading cycles don't flash the spinner or
    // blank out the card — keeping recentlyCompleted and featuredOverride visible.
    const showBody =
        !initialLoading &&
        !templatesEmpty &&
        !spawnLoading &&
        (mode !== 'gate-first' || effectiveGateDismissed);

    // ---------------------------------------------------------------------------
    // Stagger mount animation trigger
    // ---------------------------------------------------------------------------
    useEffect(() => {
        if (!showBody) {
            setBodyMounted(false);
            return undefined;
        }
        const t = setTimeout(() => setBodyMounted(true), 16);
        return () => clearTimeout(t);
    }, [showBody]);

    // ---------------------------------------------------------------------------
    // Unmount cleanup — clear the recentlyCompleted timer so that if the popup
    // closes within the 4-second ack window the orphaned timeout can't fire
    // onTaskActivity on an already-unmounted parent and trigger a stray tab switch.
    // ---------------------------------------------------------------------------
    useEffect(() => () => clearTimeout(recentlyCompletedTimerRef.current), []);

    // ---------------------------------------------------------------------------
    // Spawn-refresh sequence
    // ---------------------------------------------------------------------------
    useEffect(() => {
        if (!effectiveGateDismissed) {
            spawnRetryRef.current = 0;
            return undefined;
        }
        // Lead was already CONSULTATION_ATTENDED at mount — tasks exist, no spawn needed.
        if (skipSpawnRef.current) return undefined;
        setSpawnLoading(true);
        spawnRetryRef.current = 0;
        const t = setTimeout(() => refresh(), SPAWN_INITIAL_DELAY_MS);
        return () => clearTimeout(t);
    }, [effectiveGateDismissed]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---------------------------------------------------------------------------
    // Retry if tasks still absent after completed refresh
    // ---------------------------------------------------------------------------
    useEffect(() => {
        if (!spawnLoading || loading) return undefined;
        // Exit spawn-loading when tasks are present (either returned as items, or
        // detected as all-done via group.progress by the hook).
        if (totalCount > 0 || allDoneCount > 0) {
            setSpawnLoading(false);
            return undefined;
        }
        if (spawnRetryRef.current >= SPAWN_MAX_RETRIES) {
            setSpawnGaveUp(true);
            // If preview items are available, exit spawn-loading so the body renders
            // via the isPreview path instead of showing the V1 FollowUp fallback.
            if (templatePreviewItems.length > 0) {
                setSpawnLoading(false);
            }
            return undefined;
        }
        spawnRetryRef.current += 1;
        const t = setTimeout(() => refresh(), SPAWN_RETRY_DELAY_MS);
        return () => clearTimeout(t);
    }, [spawnLoading, loading, totalCount, allDoneCount, templatePreviewItems.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---------------------------------------------------------------------------
    // Reconcile featuredOverride against latest hook data.
    // Runs whenever `groups` updates (i.e. after every refresh).  If the override
    // now points at a completed or absent item, advance to the next active one or
    // clear it.  The dep array does NOT include featuredOverride — only `groups` —
    // so this effect can never trigger itself and cause an infinite loop.
    // ---------------------------------------------------------------------------
    useEffect(() => {
        // Purge optimistic IDs once the server confirms them — either as done/skipped,
        // OR when they reappear in the active groups (i.e. the item was reopened/undone
        // server-side).  Without the second check the id would stay in the set and
        // permanently hide a reopened item until unmount — which breaks Undo (Task #15).
        setOptimisticDoneIds(prev => {
            if (prev.size === 0) return prev;
            const serverDoneIds = new Set(done.map(i => i.id));
            const serverActiveIds = new Set([...rawToday, ...rawLater].map(i => i.id));
            const stillPending = [...prev].filter(
                id => !serverDoneIds.has(id) && !serverActiveIds.has(id)
            );
            if (stillPending.length === prev.size) return prev; // no change — avoid re-render
            return new Set(stillPending);
        });
        // Reconcile featuredOverride against the refreshed groups.
        if (!featuredOverride) return;
        const reconcilePool = [...(today || []), ...(later || [])].filter((i) => !i.completed_at);
        const stillActive = reconcilePool.find((i) => i.id === featuredOverride.id);
        if (!stillActive) {
            // Override became stale (item completed / absent after refresh) — advance.
            setFeaturedOverride(reconcilePool[0] || null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups]); // intentionally excludes featuredOverride + optimisticDoneIds to prevent loops

    // ---------------------------------------------------------------------------
    // Optimistic-done safety net. The reconcile effect above only purges
    // optimisticDoneIds when a refresh LANDS (groups change). If the bust-cache
    // refresh after a complete/skip ERRORS, groups don't change, the id is never
    // purged, and the item stays hidden in the mini-grid for the whole session
    // (while possibly still showing on the UpNext card) — a stuck, hidden item.
    // Guarantee recovery: whenever optimisticDoneIds is non-empty, arm a fallback
    // timeout that clears them if the server hasn't confirmed in time. A successful
    // reconcile shrinks the set (this effect re-runs, timer resets); only a failed/
    // missing refresh lets the timer fire — restoring the item so it's never lost.
    // Keeps the instant-hide UX (6s is well beyond a normal refresh round-trip).
    // ---------------------------------------------------------------------------
    useEffect(() => {
        if (optimisticDoneIds.size === 0) return undefined;
        const t = setTimeout(() => {
            setOptimisticDoneIds((prev) => (prev.size > 0 ? new Set() : prev));
        }, OPTIMISTIC_DONE_FALLBACK_MS);
        return () => clearTimeout(t);
    }, [optimisticDoneIds]);

    // ---------------------------------------------------------------------------
    // Early returns (all hooks above this line)
    // ---------------------------------------------------------------------------

    if (!hasFeatureAccess('tasks-v2', practiceId)) return null;

    if (initialLoading && !spawnLoading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} sx={{ color: '#078DEE' }} />
            </Box>
        );
    }

    if (templatesEmpty) return null;

    // ---------------------------------------------------------------------------
    // Gate (when rendered internally)
    // ---------------------------------------------------------------------------
    const isLeadBooked = (leadDetail?.lead_treatments || []).some(
        (t) => t.status === BOOKED_STATUS
    );

    if (!gateRenderedInHost && mode === 'gate-first' && isLeadBooked && !gateDismissed) {
        const handleGateDismissed = () => {
            setGateDismissed(true);
            const ltId =
                (leadDetail?.lead_treatments || []).find(
                    (t) => t.status === 'CONSULTATION_BOOKED'
                )?.id || leadDetail?.lead_treatments?.[0]?.id;
            if (ltId) startUndo(ltId);
        };
        return (
            <AttendanceGate
                variant="card"
                leadDetail={leadDetail}
                handleNoAttended={handleNoAttended}
                onDismissed={handleGateDismissed}
            />
        );
    }

    // ---------------------------------------------------------------------------
    // Spawn-loading state — same as v1
    // ---------------------------------------------------------------------------
    if (spawnLoading) {
        // All retries exhausted + no tasks spawned → fall back to V1 FollowUp.
        // Only fall back to V1 FollowUp when there are no preview items either —
        // if a template applies, show the preview body instead.
        if (spawnGaveUp && totalCount === 0 && templatePreviewItems.length === 0) {
            return (
                <SpawnFailureFallback
                    handleClose={handleClose}
                    onTaskActivity={onTaskActivity}
                    practiceId={practiceId}
                    leadId={leadId}
                    leadDetail={leadDetail}
                    onOpenSidebarTab={onOpenSidebarTab}
                    handleReloadLeads={handleReloadLeads}
                />
            );
        }
        const handleManualRetry = () => {
            setSpawnGaveUp(false);
            spawnRetryRef.current = 0;
            refresh();
        };
        const spawnMessage = spawnRetryRef.current >= 1
            ? 'Tasks are taking longer than usual…'
            : 'Spawning your tasks…';
        return (
            <Box
                data-testid="spawn-loading"
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3, px: 1 }}
            >
                {!spawnGaveUp && <CircularProgress size={18} sx={{ color: '#078DEE', flexShrink: 0 }} />}
                <Typography sx={{ fontSize: '13px', color: '#6e6e73', letterSpacing: '-0.01em' }}>
                    {spawnMessage}
                </Typography>
                {spawnGaveUp && (
                    <Button
                        data-testid="spawn-retry-btn"
                        size="small"
                        onClick={handleManualRetry}
                        sx={{ fontSize: '12px', color: '#078DEE', textTransform: 'none', p: 0, minWidth: 0 }}
                    >
                        Retry
                    </Button>
                )}
            </Box>
        );
    }

    if (!showBody) return null;

    // ---------------------------------------------------------------------------
    // v2 entrance helper — stats strip uses slide-down (vs v1's slide-up)
    // ---------------------------------------------------------------------------
    const statsEntranceSx = reducedMotion
        ? {}
        : {
            opacity: bodyMounted ? 1 : 0,
            transform: bodyMounted ? 'none' : 'translateY(-12px)',
            transition: `opacity 400ms ${EASING} 0ms, transform 400ms ${EASING} 0ms`,
        };

    // Stagger helper for non-stats sections (same slide-up as v1 but slightly shorter)
    const fadeSx = (delayMs) =>
        reducedMotion
            ? {}
            : {
                opacity: bodyMounted ? 1 : 0,
                transform: bodyMounted ? 'none' : 'translateY(16px)',
                transition: `opacity 400ms ${EASING} ${delayMs}ms, transform 400ms ${EASING} ${delayMs}ms`,
            };

    // ---------------------------------------------------------------------------
    // Cascade-paused — all items PAUSED (snoozed), not completed. Show a clear
    // "Paused until X" state so staff understand the workflow is snoozed, not done.
    // ---------------------------------------------------------------------------
    if (allPaused) {
        // Use server data when available; fall back to optimisticPausedInfo (set
        // synchronously on POST 200 so the card renders before the refresh lands).
        const firstPaused = done.find(i => i.completion_state === CHECKLIST_COMPLETION_STATE.PAUSED);
        const rawPausedUntil = firstPaused?.paused_until || optimisticPausedInfo?.paused_until || null;
        // paused_until may be DRF "DD-MM-YYYY HH:mm:ss.SSSSSSZ" OR ISO; parseApiDate
        // auto-detects both. DISPLAY.
        const pausedDate = rawPausedUntil ? parseApiDate(rawPausedUntil) : null;
        const pausedUntilLabel = pausedDate && pausedDate.isValid()
            ? pausedDate.format('dddd D MMMM')
            : null;
        const pausedOnTitle = firstPaused?.title || firstPaused?.taskTitle || optimisticPausedInfo?.title || null;
        // Count truly completed (non-paused) steps for the reassurance note.
        const completedCount = done.filter(i => i.completion_state !== CHECKLIST_COMPLETION_STATE.PAUSED).length;
        return (
            <Box className="gchk-root" data-testid="attended-guided-checklist-v2" data-mode={mode} sx={{ py: 2, width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'hidden', boxSizing: 'border-box' }}>
                <Box
                    data-testid="checklist-paused-card"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 4,
                        px: 3,
                        bgcolor: 'rgba(255,171,0,0.06)',
                        borderRadius: '16px',
                        border: '1px solid rgba(255,171,0,0.20)',
                        textAlign: 'center',
                    }}
                >
                    <Box
                        sx={{
                            width: 52,
                            height: 52,
                            borderRadius: '50%',
                            bgcolor: 'rgba(255,171,0,0.12)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 1.5,
                        }}
                    >
                        <Iconify icon="mdi:pause-circle" width={28} height={28} sx={{ color: '#FFAB00' }} />
                    </Box>
                    <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.022em', mb: 0.5 }}>
                        {pausedUntilLabel ? `Paused until ${pausedUntilLabel}` : 'Checklist paused'}
                    </Typography>
                    {pausedOnTitle && (
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.01em', mb: 0.5 }}>
                            Next up: {pausedOnTitle}
                        </Typography>
                    )}
                    <Typography sx={{ fontSize: '13px', color: '#6e6e73', letterSpacing: '-0.01em', mb: 2.5, maxWidth: 280 }}>
                        {completedCount > 0
                            ? `${completedCount} step${completedCount !== 1 ? 's' : ''} already done — remaining steps will resume from here.`
                            : 'Remaining steps are snoozed and will resume from here.'}
                    </Typography>
                    <Button
                        data-testid="checklist-resume-btn"
                        variant="contained"
                        disableElevation
                        onClick={handleResumeChecklist}
                        sx={{
                            ...CRM_PRIMARY_BUTTON_SX,
                            fontSize: '14px',
                            fontWeight: 600,
                            letterSpacing: '-0.01em',
                            textTransform: 'none',
                            borderRadius: '6px',
                            px: 3,
                            py: 0.875,
                        }}
                    >
                        Resume checklist
                    </Button>
                </Box>
            </Box>
        );
    }

    // ---------------------------------------------------------------------------
    // All-done — celebratory card (same as v1)
    // onCreateReminder: when provided shows the reminder CTA; falls back to Close.
    // ---------------------------------------------------------------------------
    if (allDone) {
        return (
            <Box className="gchk-root" data-testid="attended-guided-checklist-v2" data-mode={mode} sx={{ py: 2, width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'hidden', boxSizing: 'border-box' }}>
                <TasksEmptyStateCard
                    variant="all-complete"
                    onCreateReminder={onCreateReminder || null}
                    onClose={handleClose}
                />
            </Box>
        );
    }

    // ---------------------------------------------------------------------------
    // Empty items (no live tasks, no preview templates)
    // ---------------------------------------------------------------------------
    if (totalCount === 0 && templatePreviewItems.length === 0) {
        return (
            <Box className="gchk-root" data-testid="attended-guided-checklist-v2" data-mode={mode} sx={{ py: 2, width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'hidden', boxSizing: 'border-box' }}>
                <TasksEmptyStateCard
                    variant="no-tasks"
                    onCreateReminder={onCreateReminder || null}
                />
            </Box>
        );
    }

    // ---------------------------------------------------------------------------
    // Due-date override helpers — defined before effectiveUpNext usage
    // ---------------------------------------------------------------------------

    const applyDueDateOverride = (item) => {
        if (!item || !dueDateOverrides.has(item.taskId)) return item;
        return { ...item, due_at: dueDateOverrides.get(item.taskId).toISOString() };
    };

    const handleChangeDueDate = async (item, newDate) => {
        // Preview items have no taskId — spawn from template first to get a real taskId.
        if (!item?.taskId) {
            if (!item?._templateId) {
                dispatch(updateError({ success: false, message: "Due dates can't be set on this item yet." }));
                return;
            }
            const spawnResp = await callApi(
                `/api/v1/practices/${practiceId}/tasks/from-template/`,
                'post',
                { template_id: item._templateId, lead_id: leadId }
            );
            if (!spawnResp.ok && spawnResp.status !== 200 && spawnResp.status !== 201) {
                dispatch(updateError({ success: false, message: "Couldn't create tasks. Please try again." }));
                return;
            }
            const newTaskId = spawnResp.data?.id;
            if (newTaskId) {
                await callApi(`/api/v1/practices/${practiceId}/tasks/${newTaskId}/`, 'patch', { due_at: newDate.toISOString() });
                dispatch(updateError({ success: true, message: 'Tasks created and due date set.' }));
                // Setting a due date IS a task action — let the host enable Finish.
                if (onTaskActivity) onTaskActivity();
            } else {
                dispatch(updateError({ success: true, message: 'Tasks created! Select a task to set its due date.' }));
            }
            emitTasksV2Changed();
            refresh();
            return;
        }
        const allItems = [...today, ...later, ...done];
        const cascade = cascadeTaskDueDates({
            items: allItems,
            anchorTaskId: item.taskId,
            oldDueAt: item.due_at,
            newDueAt: newDate,
        });

        const prevOverrides = new Map(dueDateOverrides);
        const nextOverrides = new Map(dueDateOverrides);
        cascade.forEach(({ taskId, newDueAt: d }) => nextOverrides.set(taskId, d));
        setDueDateOverrides(nextOverrides);

        const results = await Promise.allSettled(
            cascade.map(({ taskId, newDueAt: d }) =>
                callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/`, 'patch', {
                    due_at: d.toISOString(),
                    ...(taskId === item.taskId ? { is_anchor: true } : {}),
                })
            )
        );

        const anyFailed = results.some((r) => r.status === 'rejected' || !r.value?.ok);
        if (anyFailed) {
            setDueDateOverrides(prevOverrides);
            dispatch(updateError({ success: false, message: "Couldn't update due dates. Please try again." }));
            return;
        }

        // nCascaded = tasks shifted BESIDES the anchor (the "follow-up" tail)
        const nCascaded = cascade.length - 1;
        const cascadeMessage = nCascaded > 0
            ? `Rescheduled — also moved ${nCascaded} follow-up task${nCascaded !== 1 ? 's' : ''} to keep the sequence`
            : 'Task rescheduled';
        // Use local Snackbar state — both dispatch(updateError) and enqueueSnackbar
        // render outside the fullscreen Dialog z-index and are invisible behind it.
        setRescheduledMsg(cascadeMessage);
        // Rescheduling a task is a completed action for this visit (the user isn't
        // ready to do more now) — fire IMMEDIATELY so the host sets the attended
        // outcome and the Finish button activates, same as complete/skip/pause.
        if (onTaskActivity) onTaskActivity();
        emitTasksV2Changed();
        refresh({ bustCache: true });
    };

    // ---------------------------------------------------------------------------
    // Preview-item assignment — a preview item has no taskId yet. Spawn the task
    // from its template (idempotent: the BE de-dupes a template already applied to
    // this lead), then PATCH the chosen assignees onto the resulting task. Same
    // spawn-then-patch shape as handleChangeDueDate's preview branch — no backend
    // change required. selectedUsers are PracticeUser objects from AssignedToLead.
    // ---------------------------------------------------------------------------
    const handlePreviewAssign = async (item, selectedUsers) => {
        if (!item?._templateId) {
            dispatch(updateError({ success: false, message: "Assignees can't be set on this item yet." }));
            return;
        }
        const spawnResp = await callApi(
            `/api/v1/practices/${practiceId}/tasks/from-template/`,
            'post',
            { template_id: item._templateId, lead_id: leadId }
        );
        if (!spawnResp.ok && spawnResp.status !== 200 && spawnResp.status !== 201) {
            dispatch(updateError({ success: false, message: "Couldn't create tasks. Please try again." }));
            return;
        }
        const newTaskId = spawnResp.data?.id;
        if (!newTaskId) {
            dispatch(updateError({ success: true, message: 'Tasks created! Open a task to set assignees.' }));
            refresh({ bustCache: true });
            return;
        }
        const assignResp = await callApi(
            `/api/v1/practices/${practiceId}/tasks/${newTaskId}/`,
            'patch',
            { assignees: (selectedUsers || []).map((pu) => ({ practice_user_id: pu.id })) }
        );
        if (!assignResp.ok) {
            dispatch(updateError({ success: false, message: assignResp?.data?.detail || 'Failed to update assignees' }));
        } else {
            dispatch(updateError({ success: true, message: 'Assignees updated.' }));
        }
        // Assigning is a task action for this visit — let the host enable Finish.
        if (onTaskActivity) onTaskActivity();
        refresh({ bustCache: true });
    };

    // ---------------------------------------------------------------------------
    // Active data — when no live tasks exist but templates do, treat preview items
    // identically to real spawned tasks (no preview-banner, fully interactive).
    // ---------------------------------------------------------------------------
    const isPreview = totalCount === 0 && templatePreviewItems.length > 0;
    const activeToday  = isPreview ? templatePreviewItems : today;
    const activeLater  = isPreview ? [] : later;
    const activeDone   = isPreview ? [] : done;
    const activeDoneCount  = activeDone.length;
    const activeTotalCount = activeToday.length + activeLater.length + activeDoneCount;

    const effectiveUpNext = applyDueDateOverride(
        featuredOverride || (isPreview ? (templatePreviewItems[0] || null) : upNext)
    );
    const activePool = [...activeToday, ...activeLater].filter((i) => !i.completed_at);
    const currentIdx = effectiveUpNext?.id != null
        ? activePool.findIndex((i) => i.id === effectiveUpNext.id)
        : 0;
    // When currentIdx is -1 (featured item was completed and fell out of activePool),
    // fall back to the first two remaining items so the right pane can still show something.
    const nextUpNextRaw = currentIdx >= 0
        ? (activePool[currentIdx + 1] || null)
        : (activePool[1] || activePool[0] || null);
    const nextUpNext = nextUpNextRaw ? applyDueDateOverride(nextUpNextRaw) : null;
    // Signal "All caught up" placeholder when current item is last in pool.
    const isLastInPool = currentIdx >= 0 && currentIdx === activePool.length - 1;
    const nextEmptyReason = (!nextUpNext && isLastInPool) ? 'caught_up' : null;

    const overriddenActiveGroups = {
        today: activeToday.map(applyDueDateOverride),
        later: activeLater.map(applyDueDateOverride),
        done:  activeDone.map(applyDueDateOverride),
    };
    const hasOverdue = activeToday.some((item) => item.due_at && (parseApiDate(item.due_at)?.valueOf() ?? Infinity) < Date.now());  // COMPARE
    const todayChipColor = hasOverdue ? '#FF5630' : '#FFAB00';

    // ---------------------------------------------------------------------------
    // Handlers
    // ---------------------------------------------------------------------------
    const handleItemClick = (item) => {
        if (item.completed_at) return;
        // When the user SWITCHES the featured task by clicking a different (smaller)
        // upcoming/list card, reset both right-side surfaces so a stale action view
        // doesn't linger against the newly-featured task:
        //   1. the outer host sidebar/composer (onOpenSidebarTab → overview), and
        //   2. the checklist's own ActionPanel (the flex 0 0 40% card opened from
        //      "Set Next Action") — setActionPanelItem(null) closes it.
        // Only fires on an actual switch to a different item — re-clicking the featured
        // card is a no-op. (Does NOT run on an action BUTTON click — that's handleActionClick.)
        if (item?.id !== effectiveUpNext?.id) {
            if (onOpenSidebarTab) onOpenSidebarTab('overview');
            setActionPanelItem(null);
        }
        setFeaturedOverride(item);
    };

    const handleSkip = (skippedItem) => {
        setActionPanelItem(null);
        const pool = isPreview ? templatePreviewItems : [...today, ...later];
        const remaining = pool.filter(
            (i) => !i.completed_at && i.id !== skippedItem.id
        );
        // Preview items have no real taskId/itemId — skip client-side only.
        if (!skippedItem?.taskId || !skippedItem?.itemId) {
            setFeaturedOverride(remaining[0] || null);
            return;
        }
        // Persist the skip to the BE so the item stays de-prioritised after reload.
        // Uses the same /complete/ endpoint as a full completion, with SKIPPED state.
        // Optimistic UI advance is gated on a confirmed 200 — same pattern as handlePanelComplete.
        dispatch(completeChecklistItem(
            practiceId, skippedItem.taskId, skippedItem.itemId,
            ({ success }) => {
                if (success) {
                    setOptimisticDoneIds(prev => { const s = new Set(prev); s.add(skippedItem.id); return s; });
                    setFeaturedOverride(remaining[0] || null);
                    if (onTaskActivity) onTaskActivity();
                    // Cache-bust the GET so the refetch returns real server state.
                    refresh({ bustCache: true });
                }
                // On failure the thunk already surfaced an error toast; leave the card as-is.
            },
            { source: 'upnext_skip' },
            CHECKLIST_COMPLETION_STATE.SKIPPED,
        ));
    };

    const handleActionClick = (item) => {
        if (!item) return;
        const t = item?.item_type;
        // 2026-06-13 (Raj): CONFIRMATION / YES_NO_TOGGLE now have real panels
        // (Yes/No + optional note), so their primary button OPENS the panel — the
        // same behaviour as FollowUpV2 and the treatment hub. Previously they were
        // short-circuited to a direct complete here (no Yes/No, no note), which was
        // the inconsistency. Only LINKED_CHECKLIST (genuinely no panel) still
        // completes inline from the featured card.
        if (INLINE_HANDLED_TYPES.has(t) && t !== 'CONFIRMATION' && t !== 'YES_NO_TOGGLE') {
            handlePanelComplete(item);
            return;
        }
        setActionPanelItem(item);
    };

    const handlePanelClose = () => setActionPanelItem(null);

    // messageId is forwarded from EmailActionBody.handleSendSuccess (IosEmailComposer)
    // so we can store it in response_payload for delivery-status polling.
    const handlePanelSendSuccess = (messageId) => {
        const sentItem = actionPanelItem;
        setActionPanelItem(null);
        if (!sentItem) { refresh({ bustCache: true }); return; }

        // Preview items have no real taskId — advance immediately without a completion call.
        if (!sentItem.taskId || !sentItem.itemId) {
            const pool = templatePreviewItems;
            const remaining = pool.filter((i) => !i.completed_at && i.id !== sentItem.id);
            setFeaturedOverride(remaining[0] || null);
            const label = COMPLETION_LABELS[sentItem.item_type] || DEFAULT_COMPLETION_LABEL;
            if (recentlyCompletedTimerRef.current) clearTimeout(recentlyCompletedTimerRef.current);
            setRecentlyCompleted({ completed: sentItem, label, completedAt: new Date(), fromSend: true });
            recentlyCompletedTimerRef.current = setTimeout(() => {
                setRecentlyCompleted(null);
                if (onTaskActivity) onTaskActivity();
            }, 4000);
            refresh({ bustCache: true });
            return;
        }

        // Explicitly complete the checklist item — makes the email-sent→Done transition
        // reliable, shows the green Done chip, and posts to Timeline (#16 / #20).
        // response_payload mirrors ComposerCell's markComplete shape: { channel, messageId }.
        // Gate optimistic-done + PostSendExpandedCard on the confirmed 200 — same pattern
        // as handlePanelComplete — so a BE error can't show a false "Done" confirmation.
        const SEND_CHANNEL = { WHATSAPP_SENT: 'whatsapp', SMS_SENT: 'sms' };
        const responsePayload = {
            channel: SEND_CHANNEL[sentItem.item_type] || 'email',
            messageId: messageId || null,
        };
        dispatch(completeChecklistItem(
            practiceId, sentItem.taskId, sentItem.itemId,
            ({ success }) => {
                if (success) {
                    // Mark optimistically done so the mini-grid can't flicker back to active.
                    setOptimisticDoneIds(prev => { const s = new Set(prev); s.add(sentItem.id); return s; });
                    const pool = isPreview ? templatePreviewItems : [...today, ...later];
                    const remaining = pool.filter((i) => !i.completed_at && i.id !== sentItem.id);
                    setFeaturedOverride(remaining[0] || null);
                    const label = COMPLETION_LABELS[sentItem.item_type] || DEFAULT_COMPLETION_LABEL;
                    if (recentlyCompletedTimerRef.current) clearTimeout(recentlyCompletedTimerRef.current);
                    // fromSend=true triggers PostSendExpandedCard on the right (Task #6).
                    setRecentlyCompleted({ completed: sentItem, label, completedAt: new Date(), fromSend: true });
                    // Fire onTaskActivity IMMEDIATELY (not deferred) so the host selects
                    // the 'Reminder & Follow-up' outcome and the Finish button enables.
                    // Setting activeTab='Reminder & Follow-up' keeps the checklist mounted
                    // (gate: !activeTab || activeTab === 'Reminder & Follow-up'), so this
                    // does NOT unmount the inline V2 checklist. The timeout below only
                    // clears the transient confirmation card. (mirrors handleSkip)
                    if (onTaskActivity) onTaskActivity();
                    // Clear the PostSendExpandedCard after its display window (4s).
                    recentlyCompletedTimerRef.current = setTimeout(() => {
                        setRecentlyCompleted(null);
                    }, 4000);
                }
                // Cache-bust the GET so the refetch returns real server state.
                refresh({ bustCache: true });
            },
            responsePayload,
            CHECKLIST_COMPLETION_STATE.YES,
        ));
    };

    const applyPanelCompleteSuccess = (completedItem) => {
        // Mark optimistically done so the mini-grid can't flicker back to active
        // while the busted-cache GET is in-flight.
        setOptimisticDoneIds(prev => { const s = new Set(prev); s.add(completedItem.id); return s; });
        // Advance the featured card immediately — single source of truth.
        // Mirror handleSkip: first remaining non-completed item, excluding the just-completed one.
        const pool = isPreview ? templatePreviewItems : [...today, ...later];
        const remaining = pool.filter((i) => !i.completed_at && i.id !== completedItem.id);
        setFeaturedOverride(remaining[0] || null);
        // Decorative ✓ ack — not the navigation source of truth.
        const label = COMPLETION_LABELS[completedItem.item_type] || DEFAULT_COMPLETION_LABEL;
        if (recentlyCompletedTimerRef.current) clearTimeout(recentlyCompletedTimerRef.current);
        setRecentlyCompleted({ completed: completedItem, label, completedAt: new Date() });
        // Fire onTaskActivity IMMEDIATELY so the host selects the
        // 'Reminder & Follow-up' outcome and the Finish button enables.
        // The deferred call inside setTimeout could be cancelled by a
        // re-mount before it ran, leaving Finish stuck disabled. Setting
        // activeTab='Reminder & Follow-up' satisfies the render gate, so
        // the checklist stays mounted. (mirrors handleSkip)
        if (onTaskActivity) onTaskActivity();
        // The timeout only clears the transient ✓ ack card.
        recentlyCompletedTimerRef.current = setTimeout(() => {
            setRecentlyCompleted(null);
        }, 4000);
        // Change 4: arm the banner so it appears AFTER the ack clears
        // (showRescheduleBanner is true now but the banner only renders in
        // the steady-state else-branch, which isn't visible until recentlyCompleted → null).
        setShowRescheduleBanner(true);
    };

    const handlePanelComplete = (completedItem) => {
        setActionPanelItem(null);
        if (!completedItem?.taskId || !completedItem?.itemId) { refresh({ bustCache: true }); return; }
        if (completedItem.completionAlreadyPersisted) {
            applyPanelCompleteSuccess(completedItem);
            refresh({ bustCache: true });
            return;
        }
        // Re-entrancy guard: if this (taskId, itemId) pair is already in-flight,
        // drop the duplicate invocation immediately — do NOT dispatch a second POST.
        // Key is set synchronously before the async dispatch so rapid/concurrent calls
        // are blocked even within the same event-loop tick.
        const inFlightKey = `${completedItem.taskId}:${completedItem.itemId}`;
        if (completingItemIdsRef.current.has(inFlightKey)) return;
        completingItemIdsRef.current.add(inFlightKey);

        // For LEAD_NEXT_ACTION the BE requires resolved_action in the body.
        // NextActionPanel attaches resolvedAction to the completedItem before
        // calling onComplete — forward it here so the POST includes the field.
        // The guided multi-channel follow-up sequence (FollowUpPanel) additionally
        // attaches channelOutcomes ([{channel,state}…]) — persist it as
        // response_payload.channels so the per-channel done/skipped record survives
        // (no BE migration; arbitrary response_payload is accepted).
        const payload = buildCompletionPayload(completedItem);
        dispatch(completeChecklistItem(
            practiceId, completedItem.taskId, completedItem.itemId,
            ({ success }) => {
                // Release the in-flight guard when the request settles (success or failure)
                // so the item can be re-completed if the user genuinely retries later.
                completingItemIdsRef.current.delete(inFlightKey);
                // Gate the advance + confirmation card behind a confirmed 200 from the BE.
                // Previously setRecentlyCompleted fired optimistically before the thunk,
                // so a 400/500 response still showed "Email sent" then silently reverted.
                if (success) {
                    applyPanelCompleteSuccess(completedItem);
                }
                // Cache-bust the GET so the refetch returns real server state, not the 32s-cached response.
                refresh({ bustCache: true });
            },
            payload,
        ));
    };

    // ---------------------------------------------------------------------------
    // Pause handler — opens dialog pre-filled to 7 days from now (local time)
    // ---------------------------------------------------------------------------
    const handlePause = (item) => {
        setActionPanelItem(null);
        // Reset the pause guard so this new pause dialog session is unblocked.
        // The guard is intentionally NOT released in handlePauseConfirm to avoid
        // the timing window where the Dialog is still mounted but the ref is false.
        pauseInFlightRef.current = false;
        const d = new Date();
        d.setDate(d.getDate() + 7);
        const pad = (n) => String(n).padStart(2, '0');
        const localStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00`;
        setPauseUntilDate(localStr);
        setPauseDialogItem(item);
    };

    const handlePauseConfirm = async () => {
        const item = pauseDialogItem;
        if (!item?.taskId || !item?.itemId || !pauseUntilDate) return;
        // Guard against double-fire (same pattern as completingItemIdsRef).
        if (pauseInFlightRef.current) return;
        pauseInFlightRef.current = true;
        setPauseDialogItem(null);
        const resp = await callApi(
            `/api/v1/practices/${practiceId}/tasks/${item.taskId}/checklist/${item.itemId}/pause/`,
            'post',
            { paused_until: new Date(pauseUntilDate).toISOString() }
        );
        // Guard intentionally NOT released here — the Dialog may still be mounted
        // (setPauseDialogItem(null) is batched, not yet flushed) giving a timing
        // window where a second click could pass the guard.  The guard is reset in
        // handlePause() when the user opens a new pause dialog instead.
        if (resp.ok) {
            // Set optimistic paused info FIRST — this triggers the amber Paused card
            // in the same React batch, before the async refresh or any Redux side-effect
            // can close the popup.  paused_until from the dialog input; title from item.
            setOptimisticPausedInfo({
                paused_until: new Date(pauseUntilDate).toISOString(),
                title: item.title || item.taskTitle || item.name || null,
            });
            setOptimisticDoneIds(prev => { const s = new Set(prev); s.add(item.id); return s; });
            const pool = isPreview ? templatePreviewItems : [...today, ...later];
            const remaining = pool.filter((i) => !i.completed_at && i.id !== item.id);
            setFeaturedOverride(remaining[0] || null);
            dispatch(updateError({ success: true, message: 'Task paused' }));
            emitTasksV2Changed();
            // Do NOT call onTaskActivity() here — cascade-pause is a "snoozed" state,
            // NOT a workflow completion.  onTaskActivity() tells the host popup that
            // an activity was recorded and enables Finish, which triggers the popup to
            // close before refresh() resolves and the Paused card can render.
            // The popup stays open, the refresh lands, allPaused becomes true,
            // and the amber Paused card renders.  The user dismisses via Cancel or Resume.
            refresh({ bustCache: true });
        } else {
            dispatch(updateError({ success: false, message: resp?.data?.detail || 'Failed to pause task' }));
        }
    };

    // ---------------------------------------------------------------------------
    // Undo handler — reopens a done/skipped/paused checklist item
    // ---------------------------------------------------------------------------
    const handleUndo = (item) => {
        if (!item?.taskId || !item?.itemId) return;
        dispatch(reopenChecklistItem(
            practiceId, item.taskId, item.itemId,
            ({ success }) => { if (success) refresh({ bustCache: true }); }
        ));
    };

    // Undo from the green completion ack card — same behaviour as FollowUpV2:
    // cancel the auto-dismiss timer, reopen the just-completed item, clear the ack.
    const handleAckUndo = () => {
        if (recentlyCompletedTimerRef.current) clearTimeout(recentlyCompletedTimerRef.current);
        if (recentlyCompleted?.completed) handleUndo(recentlyCompleted.completed);
        setRecentlyCompleted(null);
    };

    // Resume from cascade-paused state — reopen any one paused item (server
    // cascade-resumes all pending tasks and shifts due dates forward).
    // NB: declared as a hoisted `function` (not a const arrow) because the
    // paused-state early-return render (the "Paused until…" card's Resume button)
    // references it ABOVE this line — a const arrow would be in the temporal dead
    // zone there and throw "Cannot access 'handleResumeChecklist' before initialization".
    function handleResumeChecklist() {
        // Find a paused item from server data or the optimistic info.
        const firstPaused = done.find(i => i.completion_state === CHECKLIST_COMPLETION_STATE.PAUSED);
        if (!firstPaused?.taskId || !firstPaused?.itemId) return;
        setOptimisticPausedInfo(null); // clear optimistic state immediately so the card goes away
        dispatch(reopenChecklistItem(
            practiceId, firstPaused.taskId, firstPaused.itemId,
            ({ success }) => {
                if (success) {
                    setFeaturedOverride(null);
                    // Resume IS a genuine workflow action — fire onTaskActivity so the
                    // host popup enables Finish after the user resumes the checklist.
                    if (onTaskActivity) onTaskActivity();
                    refresh({ bustCache: true });
                }
            }
        ));
    }

    // ---------------------------------------------------------------------------
    // Guided checklist body — v2 sub-components, stats strip slides DOWN
    // ---------------------------------------------------------------------------
    return (
        <Box className="gchk-root" data-testid="attended-guided-checklist-v2" data-mode={mode} sx={{ py: 2, width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'hidden', boxSizing: 'border-box' }}>
            {/* Undo-attendance banner (gate-first only, 30s window) */}
            {showBanner && mode === 'gate-first' && (
                <UndoBanner
                    secondsLeft={secondsLeft}
                    onUndo={undo}
                    onDismiss={dismissUndo}
                />
            )}
            {/* Stats strip — full width, above split row so it never compresses when
                 the action panel opens beside the UpNext card.  */}
            <Box
                className="gchk-stats-strip"
                data-testid="stats-strip"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minHeight: 28,
                    mb: 2,
                    ...statsEntranceSx,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    {activeToday.length > 0 && (
                        <Box data-testid="stats-chip-today" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: todayChipColor, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#3c3c43', letterSpacing: '-0.005em' }}>
                                {activeToday.length} Due today
                            </Typography>
                        </Box>
                    )}
                    {activeLater.length > 0 && (
                        <Box data-testid="stats-chip-later" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#6e6e73', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#3c3c43', letterSpacing: '-0.005em' }}>
                                {activeLater.length} Later
                            </Typography>
                        </Box>
                    )}
                    {activeDoneCount > 0 && (
                        <Box data-testid="stats-chip-done" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#34c759', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#3c3c43', letterSpacing: '-0.005em' }}>
                                {activeDoneCount} Done
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Progress bar — flex:1 so it fills the remaining strip width */}
                <Box sx={{ flex: 1, maxWidth: 320, ml: 1.5, height: 4, borderRadius: '2px', bgcolor: 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                    <Box
                        data-testid="progress-bar-fill"
                        sx={{
                            height: '100%',
                            width: `${activeTotalCount > 0 ? (activeDoneCount / activeTotalCount) * 100 : 0}%`,
                            bgcolor: '#34c759',
                            borderRadius: '2px',
                            transition: 'width 400ms ease',
                        }}
                    />
                </Box>
            </Box>

            {/* Split row: UpNext left + action panel right.
                 Only UpNext lives here so the action panel doesn't compress stats/mini-grid.
                 50/50 split: equal-width columns so neither side dominates. Left column
                 stretches to match ActionPanel height via alignItems:stretch + height:100%
                 selector on the card root. The attDebug wrapper matches at 50%. */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    gap: 2,
                    '& > [data-testid="action-panel"]': { flex: '0 0 70%', height: '100%', display: 'flex', flexDirection: 'column' },
                    '@media (max-width: 900px)': {
                        '& > [data-testid="action-panel"]': { flex: '0 0 100%' },
                    },
                }}
            >
                {/* Left column — UpNext card only.
                    Equal-height with the ActionPanel: the parent row is
                    alignItems:'stretch', so this column already fills the row height;
                    we make its inner UpNext card fill that height too (height:100% via
                    the selector below) so the card grows to match a taller ActionPanel
                    instead of leaving empty space beneath it. alignSelf:'stretch' is
                    explicit for clarity. */}
                <Box
                    sx={[
                        {
                            flex: actionPanelItem ? '1 1 0' : '1',
                            minWidth: 0,
                            alignSelf: 'stretch',
                            display: 'flex',
                            flexDirection: 'column',
                            transition: `flex 320ms ${EASING}`,
                            // When the ActionPanel is open beside it, stretch the UpNext
                            // card (and the post-action swap row) to fill the column height.
                            // No explicit height on the column: an explicit height disables
                            // align-self:stretch and collapses it to content height. alignSelf:stretch
                            // fills the row; the card uses height:100% to fill this column.
                            ...(actionPanelItem ? { '& [data-testid="upnext-card-v2"]': { height: '100%' } } : {}),
                        },
                        attDebug && { position: 'relative', outline: '2px dashed #2962FF', outlineOffset: '-2px' },
                    ]}
                >
                    {attDebug && <Box component="span" sx={dbgTagSx('#2962FF')}>CHECKLIST LEFT</Box>}
                    {/* Up Next card — v2 variant, or sequenced ack window.
                        task #22: port FollowUpV2 sequenced completion to this surface.
                        During the 4s recentlyCompleted ack window: show ONLY the green
                        RecentlyCompletedCard (centered, alone) — same as FollowUpV2.jsx:2164-2178.
                        Previous behaviour showed a 30/70 simultaneous split (ack left +
                        next-hero right). After the ack clears the steady-state else-branch
                        renders the reschedule banner + UpNextCardV2 as a single beat.
                        Gate-safety: onTaskActivity() fires in handlePanelComplete/handlePanelSendSuccess
                        BEFORE setRecentlyCompleted — outcome gate is satisfied synchronously,
                        not deferred by this display window. */}
                    <Box className="gchk-upnext-wrap" sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', ...fadeSx(80) }}>
                        {/* Reschedule banner — hidden during ack window, shown in steady-state.
                            showRescheduleBanner is armed by handlePanelComplete success.
                            Opacity-only styling — no transform. */}
                        {!recentlyCompleted && showRescheduleBanner && effectiveUpNext && (
                            <Box
                                data-testid="reschedule-banner"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 1.5,
                                    px: 2,
                                    py: 1.25,
                                    bgcolor: 'rgba(7,141,238,0.06)',
                                    border: '1px solid rgba(7,141,238,0.18)',
                                    borderRadius: '10px',
                                    flexWrap: 'wrap',
                                    rowGap: '8px',
                                }}
                            >
                                <Iconify
                                    icon="mdi:calendar-clock"
                                    width={16}
                                    height={16}
                                    sx={{ color: '#078DEE', flexShrink: 0 }}
                                />
                                <Typography sx={{
                                    flex: 1,
                                    fontSize: '12px',
                                    color: '#3c3c43',
                                    letterSpacing: '-0.005em',
                                    minWidth: 0,
                                }}>
                                    Would you like to reschedule or assign your next task?
                                </Typography>
                                {effectiveUpNext?.taskId && practiceId && (
                                    <Box data-testid="reschedule-banner-assignee" sx={{ flexShrink: 0 }}>
                                        <TaskAssigneeHeads
                                            practiceId={practiceId}
                                            taskId={effectiveUpNext.taskId}
                                            initialAssignees={effectiveUpNext.rawTask?.assignees || []}
                                            leadId={leadId}
                                            onLeadTasksReassigned={() => refresh({ bustCache: true })}
                                        />
                                    </Box>
                                )}
                                <Button
                                    data-testid="reschedule-banner-btn"
                                    size="small"
                                    variant="contained"
                                    disableElevation
                                    onClick={() => setBannerRescheduleOpen(true)}
                                    sx={{
                                        ...CRM_PRIMARY_BUTTON_SX,
                                        fontSize: '11px',
                                        py: 0.4,
                                        px: 1.25,
                                        borderRadius: '6px',
                                        flexShrink: 0,
                                    }}
                                >
                                    Reschedule
                                </Button>
                                <IconButton
                                    data-testid="reschedule-banner-dismiss"
                                    size="small"
                                    aria-label="Dismiss"
                                    onClick={() => setShowRescheduleBanner(false)}
                                    sx={{ color: '#6e6e73', p: 0.25, flexShrink: 0, '&:hover': { color: '#1d1d1f' } }}
                                >
                                    <Iconify icon="eva:close-fill" width={16} height={16} />
                                </IconButton>
                                <RescheduleDialog
                                    open={bannerRescheduleOpen}
                                    onClose={() => setBannerRescheduleOpen(false)}
                                    onConfirm={async (newDate) => {
                                        const ok = await handleChangeDueDate(effectiveUpNext, newDate);
                                        if (ok !== false) setShowRescheduleBanner(false);
                                        return ok;
                                    }}
                                    item={effectiveUpNext}
                                />
                            </Box>
                        )}
                        {effectiveUpNext && (
                            <Box sx={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ visibility: recentlyCompleted ? 'hidden' : 'visible', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    <UpNextCardV2
                                        key={effectiveUpNext?.id ?? 'none'}
                                        item={effectiveUpNext}
                                        leadEmail={leadDetail?.email || leadDetail?.lead_contact?.email || ''}
                                        onActionClick={handleActionClick}
                                        onSkip={handleSkip}
                                        onPause={handlePause}
                                        onChangeDueDate={handleChangeDueDate}
                                        nextItem={nextUpNext}
                                        nextEmptyReason={nextEmptyReason}
                                        onItemClick={handleItemClick}
                                        practiceId={practiceId}
                                        panelOpen={!!actionPanelItem}
                                        onPreviewAssign={handlePreviewAssign}
                                        onMarkComplete={handlePanelComplete}
                                        leadId={leadId}
                                        onLeadTasksReassigned={() => refresh({ bustCache: true })}
                                    />
                                </Box>
                                {recentlyCompleted && (
                                    <Box data-testid="upnext-ack-window" sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
                                        <RecentlyCompletedCard
                                            item={recentlyCompleted.completed}
                                            label={recentlyCompleted.label}
                                            onDismiss={() => setRecentlyCompleted(null)}
                                            onUndo={handleAckUndo}
                                            fillHeight
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}
                        {recentlyCompleted && !effectiveUpNext && (
                            <Box data-testid="upnext-ack-window">
                                <RecentlyCompletedCard
                                    item={recentlyCompleted.completed}
                                    label={recentlyCompleted.label}
                                    onDismiss={() => setRecentlyCompleted(null)}
                                    onUndo={handleAckUndo}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Right column — action panel. When attDebug is on, wrap it in a
                    relative Box that carries a dashed outline + "ACTION PANEL" label so
                    the right side of the checklist split is visible. Rendered bare
                    (no wrapper) when off, so layout is unchanged. */}
                {actionPanelItem && (() => {
                    const panel = (
                        <ActionPanel
                            // Keyed by item id — forces a remount on item switch so
                            // ActionPanel's mount-only template-resolve effects (and
                            // any panel it routes to, e.g. FollowUpPanel) don't keep
                            // running against a stale, previously-actioned item. See
                            // AttendedGuidedChecklist.jsx for the full rationale.
                            key={actionPanelItem?.id ?? 'none'}
                            item={actionPanelItem}
                            practiceId={practiceId}
                            leadId={leadId}
                            leadDetail={leadDetail}
                            onClose={handlePanelClose}
                            onSendSuccess={handlePanelSendSuccess}
                            onComplete={handlePanelComplete}
                            handleReloadLeads={handleReloadLeads}
                            onOpenSidebarTab={onOpenSidebarTab}
                            callScriptCategory="TCO"
                        />
                    );
                    if (!attDebug) return panel;
                    // Wrapper occupies the same 50% slot ActionPanel would take as a
                    // direct flex child, and forces the inner panel to fill it, so the
                    // outline matches the real panel region.
                    return (
                        <Box
                            sx={{
                                flex: '0 0 70%',
                                minWidth: 0,
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                outline: '2px dashed #D50000',
                                outlineOffset: '-2px',
                                '& > [data-testid="action-panel"]': { flex: '1 1 auto', width: '100%' },
                                '@media (max-width: 900px)': { flex: '0 0 100%' },
                            }}
                        >
                            <Box component="span" sx={dbgTagSx('#D50000')}>ACTION PANEL</Box>
                            {panel}
                        </Box>
                    );
                })()}
            </Box>

            {/* Mini-grid — full width, below split row so it never compresses when
                 the action panel is open beside the UpNext card. */}
            <Box className="gchk-list-wrap" sx={{ mt: 3, ...fadeSx(160) }}>
                <ChecklistMiniGrid
                    groups={overriddenActiveGroups}
                    featuredItem={effectiveUpNext}
                    onItemClick={handleItemClick}
                    onUndo={handleUndo}
                    onMarkComplete={handlePanelComplete}
                />
            </Box>

            {/* Pause dialog — date picker for paused_until */}
            <Dialog
                data-testid="pause-dialog-v2"
                open={pauseDialogItem != null}
                onClose={() => setPauseDialogItem(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6e6e73', mb: 0.5 }}>
                        Pause task
                    </Typography>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em' }}>
                        {pauseDialogItem?.title || 'Pause task'}
                    </Typography>
                </Box>
                <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
                    <Typography sx={{ fontSize: '13px', color: '#3c3c43', mb: 1.5, letterSpacing: '-0.005em' }}>
                        Resume this task on:
                    </Typography>
                    <input
                        type="datetime-local"
                        value={pauseUntilDate}
                        onChange={(e) => setPauseUntilDate(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '10px 12px',
                            fontSize: '14px',
                            borderRadius: '8px',
                            border: '1.5px solid rgba(0,0,0,0.15)',
                            fontFamily: 'inherit',
                            color: '#1d1d1f',
                            outline: 'none',
                            boxSizing: 'border-box',
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 1.75, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                    <Button
                        onClick={() => setPauseDialogItem(null)}
                        sx={{ color: '#6e6e73', textTransform: 'none', fontSize: '14px', fontWeight: 500 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        data-testid="pause-confirm-btn-v2"
                        variant="contained"
                        disableElevation
                        disabled={!pauseUntilDate}
                        onClick={handlePauseConfirm}
                        sx={{ ...CRM_PRIMARY_BUTTON_SX, textTransform: 'none', fontSize: '14px', fontWeight: 600, py: 0.75, px: 2.5, borderRadius: '8px' }}
                    >
                        Pause task
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Inline reschedule success toast — rendered inside the component so it
                shares the popup's stacking context and is always visible above the Dialog. */}
            <Snackbar
                data-testid="reschedule-toast"
                open={!!rescheduledMsg}
                onClose={() => setRescheduledMsg(null)}
                autoHideDuration={4000}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                message={rescheduledMsg}
                ContentProps={{
                    sx: {
                        bgcolor: '#1d1d1f',
                        color: '#fff',
                        fontSize: '13px',
                        fontWeight: 500,
                        borderRadius: '8px',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                    },
                }}
            />
        </Box>
    );
}

AttendedGuidedChecklistV2.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadDetail: PropTypes.object,
    mode: PropTypes.oneOf(['gate-first', 'post-attended']).isRequired,
    handleClose: PropTypes.func,
    handleNoAttended: PropTypes.func,
    gateRenderedInHost: PropTypes.bool,
    externalGateDismissed: PropTypes.bool,
    /** Bumps to force a server-side refetch (e.g. after RecordAttended landed). */
    externalRefreshKey: PropTypes.number,
    /** Deferred table reload — called ~3s after archive dispatched so Celery drains first. */
    handleReloadLeads: PropTypes.func,
    /** Fires after a task is completed, sent, or skipped server-side. Host pops Finish open. */
    onTaskActivity: PropTypes.func,
    /** Called with 'email' or 'whatsapp' to open the matching right-sidebar tab in the host popup. */
    onOpenSidebarTab: PropTypes.func,
    /** When provided, the all-done / no-tasks card shows a "Create a custom reminder" CTA
     *  instead of the legacy Close button. Pass () => setShowCustomReminder(true) from the host. */
    onCreateReminder: PropTypes.func,
};

AttendedGuidedChecklistV2.defaultProps = {
    leadDetail: null,
    handleClose: null,
    handleNoAttended: null,
    gateRenderedInHost: false,
    externalGateDismissed: false,
    externalRefreshKey: 0,
    onTaskActivity: null,
    onOpenSidebarTab: null,
    onCreateReminder: null,
};
